import {
  BugReportOutlined as BugReportOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  EditDocument as EditDocumentIcon,
  EditNoteOutlined as EditNoteOutlinedIcon,
  FactCheckOutlined as FactCheckOutlinedIcon,
  MergeTypeOutlined as MergeTypeOutlinedIcon,
  NotesOutlined as NotesOutlinedIcon,
  PlayCircleOutlined as PlayCircleOutlineIcon,
  SlideshowOutlined as SlideshowOutlinedIcon,
} from "@/components/icons";
import * as React from "react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  Link as MuiLink,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useActiveOrg } from "@/features/admin/orgContext";
import { resolveTargetOrg } from "@/features/sessions/targetOrg";
import { useTranslation } from "react-i18next";
import { accountForTurns } from "@/features/account-link/accountForTurns";

import { api } from "@/api/client";
import { useAvailableLlmProviders } from "@/api/llmProviders";

import { usePageTitle } from "@/hooks/usePageTitle";

import {
  useAgentProviderModels,
  useAgentProviders,
  useAnthropicAccounts,
  useAnthropicCentralBilling,
  useCodexModels,
  useCreateSession,
  useGithubAccounts,
  useGithubCentralBilling,
  useIntents,
  useMe,
  useOpenAIAccounts,
  useServerBilling,
  useViewableDocuments,
} from "@/api/hooks";
import type {
  Intent,
  IntentSpec,
  ReasoningEffort,
  ReasoningSummary,
  ServiceTier,
  Verbosity,
} from "@/api/types";
import {
  blockedHint,
  blockedLabel,
  CUSTOM_MODEL_CLIS,
  customModelOptions,
  hasPickableCustomModel,
  parseCustomModelValue,
} from "@/features/sessions/customModels";
import { translateApiError } from "@/features/sessions/errorMessages";
import { outputLanguageForUi } from "@/features/sessions/outputLanguage";
import {
  FALLBACK_REASONING_EFFORTS,
  reasoningEffortLabel,
  reasoningSummaryLabel,
  THINKING_EFFORTS,
  verbosityLabel,
} from "@/features/sessions/reasoningEfforts";
import {
  extractErrorDetail,
  extractMissingProvider,
} from "@/features/sessions/errors";
import { ServiceTierField } from "@/features/sessions/ServiceTierField";
import { SHOW_UNSET_SELECT } from "@/features/sessions/selectProps";
import { PLACEHOLDER_SESSION_TITLE } from "@/features/sessions/sessionTitle";
import { LEARN_VIDEO_URL } from "@/features/sessions/learnVideo";
import { firstNameOf } from "@/features/sessions/firstName";
import { repoTokens } from "@/theme/tokens";

const { startPage } = repoTokens;

/** The intent grid's columns: up to ``n`` columns, each at most the
 *  card's maximum width, from the container width at which ``n`` cards of
 *  ``width`` fit. */
function gridColumnsFrom(n: number, width: number): [string, { gridTemplateColumns: string }] {
  const gaps = (n - 1) * startPage.cardGap;
  return [
    `@container (min-width: ${n * width + gaps}px)`,
    { gridTemplateColumns: `repeat(${n}, minmax(0, ${startPage.cardMaxWidth}px))` },
  ];
}
const intentGridColumns = {
  gridTemplateColumns: `minmax(0, ${startPage.cardMaxWidth}px)`,
  ...Object.fromEntries([
    gridColumnsFrom(2, startPage.cardMinWidth),
    gridColumnsFrom(3, startPage.cardMinWidth),
    gridColumnsFrom(4, startPage.cardMaxWidth),
  ]),
};

const REASONING_SUMMARY: readonly (ReasoningSummary | "")[] = [
  "",
  "auto",
  "concise",
  "detailed",
  "none",
] as const;
const VERBOSITY: readonly (Verbosity | "")[] = ["", "low", "medium", "high"] as const;
const ALL_EFFORTS = FALLBACK_REASONING_EFFORTS;

// extractMissingProvider / extractErrorDetail live in ./errors so
// they can be reused without forcing a SessionsIndex import cycle.

/** Modify intents (``document_modify`` / ``presentation_modify``)
 *  share the same UI surface as their generate counterparts —
 *  picking the method radio flips the intent between them without
 *  forcing the user back to the IntentCard grid. */

function isModifyIntent(intent: Intent): boolean {
  return intent === "document_modify" || intent === "presentation_modify";
}

/** Format the modify intent operates on — drives the primary
 *  picker's filter so the dropdown only shows files the matching
 *  skill can edit (docx skill needs DOCX, pptx skill needs PPTX). */
function modifyFormat(intent: Intent): "docx" | "pptx" | undefined {
  if (intent === "document_modify") return "docx";
  if (intent === "presentation_modify") return "pptx";
  return undefined;
}

/** UI grouping for the IntentCard grid — a presentation choice the SPA
 *  owns, outside the ``IntentSpec`` shape. Four categories so the picker
 *  reads as four questions: talk about a project, generate a
 *  deliverable, work on a document you already have, or ship code. The
 *  grid has no headings: the order keeps a category together, and each
 *  card names its category in a tag. */
type IntentCategory = "chat" | "generate" | "modify" | "code";

const INTENT_CATEGORIES: Record<Intent, IntentCategory> = {
  summarize: "chat",
  document: "generate",
  presentation: "generate",
  document_modify: "modify",
  presentation_modify: "modify",
  review: "code",
  draft_pr: "code",
  draft_issue: "code",
};
const INTENT_CATEGORY_ORDER: readonly IntentCategory[] = ["chat", "generate", "modify", "code"];

/** The card's category tag: one short word each (Chat / Generate /
 *  Document / Code). */
const INTENT_CATEGORY_TAG: Record<IntentCategory, string> = {
  chat: "new_session.intent_category_chat",
  generate: "new_session.intent_category_generate",
  modify: "new_session.intent_category_document",
  code: "new_session.intent_category_code",
};


export function SessionsIndex(): React.JSX.Element {
  const { t: tTitle } = useTranslation();
  usePageTitle(tTitle("nav.sessions", "Sessions"));
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const createSession = useCreateSession();
  // Which org this session will belong to — resolved with the SAME
  // precedence the backend uses and then SENT explicitly below, so the
  // org shown on the form is the org the session lands in. Previously
  // the request carried no org and the backend fell back on its own,
  // which is how a session could be created in a tenant while the user
  // believed root was selected.
  const activeOrg = useActiveOrg();
  const targetOrg = React.useMemo(
    () => resolveTargetOrg(activeOrg.slug, activeOrg.orgs),
    [activeOrg.slug, activeOrg.orgs],
  );
  const accounts = useOpenAIAccounts();
  const billing = useServerBilling();
  const anthropicAccounts = useAnthropicAccounts();
  const anthropicBilling = useAnthropicCentralBilling();
  const githubAccounts = useGithubAccounts();
  const githubBilling = useGithubCentralBilling();
  const intents = useIntents();
  const codexModels = useCodexModels();
  const agentProviders = useAgentProviders();
  // The account the first turn will run on (preferred, else newest
  // active): pinning any other one made that turn "repoint" the session.
  const activeAccount = accountForTurns(accounts.data);
  const serverBilling = billing.data?.server_billing_available ?? false;
  const activeAnthropicAccount = (anthropicAccounts.data ?? []).find(
    (a) => a.status === "active",
  );
  const anthropicCentralBilling =
    anthropicBilling.data?.central_billing ?? false;
  const activeGithubAccount = (githubAccounts.data ?? []).find(
    (a) => a.status === "active",
  );
  const githubCentralBilling =
    githubBilling.data?.central_billing ?? false;

  // Provider picker: only meaningful when more than one provider is
  // registered. Today only codex is, so the picker is hidden and
  // the default slug is used implicitly for the rest of the form.
  // useMemo so ``?? []`` doesn't churn the downstream deps each render.
  const providers = useMemo(
    () => agentProviders.data ?? [],
    [agentProviders.data],
  );

  // Custom LLM providers available to this org (FW-19 phase 2), fetched
  // up here — ahead of ``model``/``providerSlug`` state — because
  // ``providerEnabled`` below needs to know, per agent CLI, whether a
  // custom model exists for it: a member with no Anthropic link must
  // still be able to pick Claude Code when the org has a Bedrock
  // provider, same as a member with no OpenAI link picking Codex
  // against an OpenRouter provider.
  const availableCustom = useAvailableLlmProviders(targetOrg?.slug);
  const hasCustomModelFor = useCallback(
    // Only a model that can be picked now counts: an offline local model,
    // or one without tool calling, is listed, disabled, but can't run a
    // session.
    (cli: string): boolean => hasPickableCustomModel(availableCustom.data ?? [], cli),
    [availableCustom.data],
  );

  // Real built-in credentials only — a personal account link or a
  // deployment-wide central billing key. This is the map that gates
  // whether the BUILT-IN model (the one ``model`` holds absent a custom
  // pick) can actually run; it must NOT count a custom model, or
  // ``canCreate`` would read true while the built-in default is still
  // selected and no custom model has been picked — the backend then
  // 409s ``missing_provider_credential:<slug>`` on submit.
  const builtinProviderEnabled = useMemo<Record<string, boolean>>(() => {
    return {
      codex: Boolean(activeAccount) || serverBilling,
      claude_code: Boolean(activeAnthropicAccount) || anthropicCentralBilling,
      copilot: Boolean(activeGithubAccount) || githubCentralBilling,
    };
  }, [
    activeAccount,
    serverBilling,
    activeAnthropicAccount,
    anthropicCentralBilling,
    activeGithubAccount,
    githubCentralBilling,
  ]);

  // Per-provider picker-option gate: each provider option in the agent
  // dropdown is enabled when EITHER real credentials exist OR the org
  // has a custom model for that CLI — the picker's job is only to let
  // the member pick a usable agent, not to say the built-in default is
  // runnable without a pick. Used only for the dropdown's enablement
  // (``isProviderEnabled`` below) — ``canCreate`` reads
  // ``builtinProviderEnabled`` instead.
  const providerEnabled = useMemo<Record<string, boolean>>(() => {
    const enabled: Record<string, boolean> = { ...builtinProviderEnabled };
    for (const cli of CUSTOM_MODEL_CLIS) {
      enabled[cli] = Boolean(enabled[cli]) || hasCustomModelFor(cli);
    }
    return enabled;
  }, [builtinProviderEnabled, hasCustomModelFor]);
  const isProviderEnabled = (slug: string): boolean =>
    providerEnabled[slug] ?? true;

  // The "best" provider to land on at form-open time. Preference order:
  //
  //   1. The backend-marked default IF the user has it enabled.
  //   2. The first registered provider that's enabled (the only-Copilot-
  //      linked case lands here today — Codex is registered first and
  //      marked default but no OpenAI link → fall through to Copilot).
  //   3. The backend-marked default regardless of enablement (no enabled
  //      providers at all → keep the would-be default visible so the
  //      "no credentials" banner names a real runtime).
  //   4. First registered, ultimately "codex" as a string-default.
  //
  // Recomputed when either the provider list OR the enablement map
  // changes — credential queries resolve asynchronously after the
  // providers fetch, so we want to re-seed once we know what's
  // actually usable.
  const defaultProviderSlug = useMemo(() => {
    const defaultEnabled = providers.find(
      (p) => p.default && providerEnabled[p.slug],
    );
    if (defaultEnabled) return defaultEnabled.slug;
    const firstEnabled = providers.find((p) => providerEnabled[p.slug]);
    if (firstEnabled) return firstEnabled.slug;
    return (
      providers.find((p) => p.default)?.slug
      ?? providers[0]?.slug
      ?? "codex"
    );
  }, [providers, providerEnabled]);

  const [providerSlug, setProviderSlug] = useState<string>(defaultProviderSlug);
  // Locks the picker to the user's explicit choice. Without this, the
  // re-seed effect below would yank the picker back to the auto-default
  // every time the enablement map mutates (e.g. a credential query
  // refetches and momentarily flips a provider's enabled state).
  const [providerTouched, setProviderTouched] = useState(false);
  // Re-seed when the providers / enablement load:
  //   * On first paint ``providerSlug`` may capture a disabled
  //     ``default`` slug — re-seed to the first enabled provider as
  //     soon as we know one exists.
  //   * If the current slug isn't in the registry (e.g. the backend
  //     stopped registering it across a deploy), fall back too.
  //   * Once the user has explicitly picked, leave them alone.
  useEffect(() => {
    if (!agentProviders.data) return;
    if (providerTouched) return;
    const slugValid = providers.some((p) => p.slug === providerSlug);
    const slugEnabled = providerEnabled[providerSlug] ?? false;
    if ((!slugValid || !slugEnabled) && providerSlug !== defaultProviderSlug) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProviderSlug(defaultProviderSlug);
    }
  }, [
    agentProviders.data,
    providers,
    providerSlug,
    providerEnabled,
    providerTouched,
    defaultProviderSlug,
  ]);

  // Whether the CURRENTLY SELECTED provider's real built-in credentials
  // exist — deliberately NOT ``isProviderEnabled`` (that map also counts
  // a custom model, which only unblocks the picker, not the built-in
  // default). Drives both the banner (shown when the picker is hidden
  // on a single-provider deployment, or when it's visible but the
  // selected provider has no credentials) and ``canCreate`` below.
  const builtinCredentialOk = builtinProviderEnabled[providerSlug] ?? true;

  // Provider-scoped model catalog. Replaces the legacy
  // ``useCodexModels()`` call that drove the model dropdown — we
  // still subscribe to the codex catalog above for the codex-specific
  // reasoning_effort / summary / verbosity panels (those fields don't
  // exist on the provider-neutral ``AgentModel`` shape directly, only
  // under ``enums``/``defaults`` which the panel below maps from the
  // legacy hook for now).
  const providerModels = useAgentProviderModels(providerSlug);
  const providerModelList = useMemo(
    () => providerModels.data?.models ?? [],
    [providerModels.data?.models],
  );
  const providerDefaultModel = providerModels.data?.default ?? "";

  // The first card is selected by default: the catalog's first intent in
  // the grid's order, until the user picks one.
  const orderedIntents = useMemo(
    () =>
      INTENT_CATEGORY_ORDER.flatMap((category) =>
        (intents.data ?? []).filter((s) => INTENT_CATEGORIES[s.intent] === category),
      ),
    [intents.data],
  );
  // ``?intent=<intent>`` preselects a card (the two-step start's "Create a
  // document" lands here with ``document``); an intent the catalog doesn't
  // offer is ignored.
  const [searchParams] = useSearchParams();
  const askedIntent = searchParams.get("intent");
  const linkedIntent = orderedIntents.find((s) => s.intent === askedIntent)?.intent ?? null;
  const [pickedIntent, setIntent] = useState<Intent | null>(null);
  const intent: Intent = pickedIntent ?? linkedIntent ?? orderedIntents[0]?.intent ?? "summarize";
  const me = useMe();
  const firstName = firstNameOf(me.data?.user?.display_name);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [model, setModel] = useState("");
  // Whether the user picked a model themselves (reset by a provider
  // change). Until then, a user with no built-in credentials starts on a
  // custom model, so Create works without opening the model settings.
  const [modelTouched, setModelTouched] = useState(false);
  const customOptions = useMemo(
    () => customModelOptions(availableCustom.data ?? [], providerSlug),
    [availableCustom.data, providerSlug],
  );
  const customPick = parseCustomModelValue(model);
  const pickedCustom = customOptions.find((o) => o.value === model);
  // A custom model runs on its provider's key through the gateway, so the
  // user's own built-in credentials don't gate it. A local model needs its
  // connector online, and every custom model needs tool calling.
  const canCreate =
    builtinCredentialOk || (customPick !== null && (pickedCustom === undefined || pickedCustom.blocked === null));
  const [effort, setEffort] = useState<ReasoningEffort | "">("");
  const [summary, setSummary] = useState<(typeof REASONING_SUMMARY)[number]>("");
  const [verbosity, setVerbosity] = useState<(typeof VERBOSITY)[number]>("");
  // Empty string = "harness default" (resolves to flex server-side);
  // explicit "fast" opts up to priority routing at session creation.
  const [serviceTier, setServiceTier] = useState<ServiceTier | "">("");
  // Anthropic extended-thinking effort (claude_code only). Surfaced
  // as a select; "" = unset (no override — runtime decides), enum
  // values map to ``--effort <level>`` on Claude Code's CLI.
  const [thinkingEffort, setThinkingEffort] = useState<
    "" | "low" | "medium" | "high" | "xhigh" | "max"
  >("");
  // Inline 409 surfaced after a create-session attempt against a
  // provider whose credentials disappeared between picker render and
  // submit (e.g. the user revoked the link in another tab). The
  // payload's slug — pulled out of ``missing_provider_credential:<slug>``
  // — drives the message + deep-link.
  const [credentialError, setCredentialError] = useState<string | null>(null);
  // Inline 422/403 surfaced after a create-session attempt that
  // fails the primary-document validation chain (missing /
  // mismatch / not viewable). The detail string runs through the
  // i18n helper from Task 6 so the user sees translated copy.
  const [createError, setCreateError] = useState<string | null>(null);
  // Selected primary document for modify intents. Reset by the
  // method-radio + intent handlers when the format the picker is
  // filtered on changes so a stale id from the other format can't
  // ride into the create payload. Avoids the eslint
  // setState-in-effect anti-pattern by scrubbing inline at the
  // event sites instead of via a useEffect on requiredFormat.
  const [primarySource, setPrimarySource] = useState<"upload" | "library">("upload");
  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  const [primaryDocumentId, setPrimaryDocumentId] = useState<string | null>(
    null,
  );
  const selectIntent = (next: Intent): void => {
    setIntent(next);
    // Scrub a stale primary doc when the new intent has a different (or
    // no) modify format — prevents a docx id riding into a pptx-modify
    // create payload, and clears it outright when the user picks a
    // non-modify intent.
    if (modifyFormat(next) !== modifyFormat(intent)) {
      setPrimaryDocumentId(null);
    }
    setCreateError(null);
  };
  const onIntentGridKey = (e: React.KeyboardEvent<HTMLElement>): void => {
    const count = orderedIntents.length;
    if (count === 0) return;
    const at = Math.max(0, orderedIntents.findIndex((s) => s.intent === intent));
    const to =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? (at + 1) % count
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? (at - 1 + count) % count
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? count - 1
              : null;
    if (to === null) return;
    e.preventDefault();
    const next = orderedIntents[to];
    if (!next) return;
    // Re-render first, so the newly chosen card is the group's one
    // focusable radio when it takes focus.
    flushSync(() => selectIntent(next.intent));
    e.currentTarget
      .querySelector<HTMLElement>(`[data-testid="intent-card-${next.intent}"]`)
      ?.focus();
  };
  // Viewable documents filtered by the active modify format. Hook
  // is keyed on the format so switching document_modify ↔
  // presentation_modify just reads from the other slot — no double
  // fetch when the user toggles back.
  const requiredFormat = modifyFormat(intent);
  // Only the TARGET org's documents: the session lands there and may
  // edit only its own org's documents (``document_org_mismatch``). With
  // no app-bar pin the list would otherwise span every org.
  const viewableDocs = useViewableDocuments(requiredFormat, targetOrg?.slug);
  const modelInfo = (codexModels.data?.models ?? []).find((m) => m.slug === model);
  const effortOptions: readonly (ReasoningEffort | "")[] = modelInfo
    ? ["", ...modelInfo.supported_reasoning_levels]
    : ["", ...ALL_EFFORTS];
  const isCodex = providerSlug === "codex";
  // Provider-agnostic feature probe: check whether the picked
  // model declares ``extended_thinking``. Used to gate the
  // thinking-budget select — Haiku 4.5 is the current
  // counter-example among Anthropic's catalog.
  const agentModelInfo = providerModelList.find((m) => m.slug === model);
  const supportsExtendedThinking = Boolean(
    agentModelInfo?.features.includes("extended_thinking"),
  );

  // Re-seed the model when the provider changes — the previously
  // selected slug belongs to a different catalog, so we drop it back
  // to the new provider's default the moment its catalog resolves.
  useEffect(() => {
    if (!providerModels.data) return;
    // No built-in credentials: the built-in default couldn't run, a
    // custom model can. Prefer one that passed the tool Test. A blocked
    // model (offline, or without tool calling) is never picked, and a pick
    // that became blocked falls back like a model that left the list.
    const pickable = customOptions.filter((o) => o.blocked === null);
    const firstCustom =
      pickable.find((o) => o.supportsTools === true) ?? pickable[0];
    if (!modelTouched && !builtinCredentialOk && firstCustom !== undefined) {
      if (pickable.some((o) => o.value === model)) return;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setModel(firstCustom.value);
      return;
    }
    const stillValid =
      providerModelList.some((m) => m.slug === model) ||
      pickable.some((o) => o.value === model);
    if (stillValid) return;
    setModel(providerDefaultModel);
  }, [
    providerModels.data,
    providerModelList,
    providerDefaultModel,
    model,
    customOptions,
    modelTouched,
    builtinCredentialOk,
  ]);
  useEffect(() => {
    if (effort !== "" || !modelInfo) return;
    if (modelInfo.supported_reasoning_levels.includes("medium")) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEffort("medium");
    } else if (modelInfo.default_reasoning_level) {
      setEffort(modelInfo.default_reasoning_level);
    }
  }, [effort, modelInfo]);

  return (
    <Stack
      // Fills the centre panel, so the org footnote sits at its bottom.
      data-fill-panel=""
      sx={{ flex: "1 0 auto", gap: 1.5, minWidth: 0 }}
    >
      {/* The greeting and intro, with more room below them than between
          the other blocks of the page. While the setup reminder takes its
          row above the panels (AppShell marks the centre panel), the extra
          room goes, so every card and Start still fit a 1360x900 window. */}
      <Box sx={{ mb: 1.5, "[data-setup-nudge] > [data-fill-panel] > &": { mb: 0 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          sx={{ gap: { xs: 1.5, sm: 3 }, alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Typography variant="h4" component="h1" sx={{ minWidth: 0 }}>
            {firstName
              ? t("new_session.greeting", { name: firstName })
              : t("new_session.greeting_no_name")}
          </Typography>
          {/* Only once there is a video to link to (learnVideo.ts). */}
          {LEARN_VIDEO_URL && (
            <Button
              component="a"
              href={LEARN_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              color="success"
              size="small"
              startIcon={<PlayCircleOutlineIcon />}
              sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "auto" }, whiteSpace: "nowrap" }}
            >
              {t("new_session.tour_button")}
            </Button>
          )}
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          {t("new_session.intro")}
        </Typography>
      </Box>
      {!canCreate && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ py: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate("/app/account")}>
              {t("session.providerPicker.link_account_button")}
            </Button>
          }
        >
          {isCodex
            ? t("session.providerPicker.missing_codex_alert")
            : t("session.providerPicker.missing_provider_alert", {
                provider:
                  providers.find((p) => p.slug === providerSlug)
                    ?.display_name ?? providerSlug,
              })}
          {hasCustomModelFor(providerSlug) && (
            <> {t("session.providerPicker.custom_model_hint")}</>
          )}
        </Alert>
      )}
      {customPick && !builtinCredentialOk && (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          {t("session.providerPicker.custom_model_selected", {
            model: customOptions.find((o) => o.value === model)?.label ?? customPick.slug,
            provider:
              customOptions.find((o) => o.value === model)?.providerName ?? "",
          })}
        </Alert>
      )}
      {isCodex && !activeAccount && serverBilling && (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          {t("new_session.central_billing_note")}
        </Alert>
      )}
      {credentialError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/app/account?provider=${credentialError}`)}
            >
              {t("session.providerPicker.missing_credential_link")}
            </Button>
          }
          onClose={() => setCredentialError(null)}
        >
          {t("session.providerPicker.missing_credential_alert", {
            provider:
              providers.find((p) => p.slug === credentialError)
                ?.display_name ?? credentialError,
          })}{" "}
          <MuiLink
            component="button"
            onClick={() => navigate(`/app/account?provider=${credentialError}`)}
            sx={{ verticalAlign: "baseline" }}
          >
            {t("session.providerPicker.missing_credential_link")}
          </MuiLink>
          .
        </Alert>
      )}

      {/* One grid, no category headings: each card names its category in
          a tag instead. Equal rows (1fr), so every card is as tall as the
          tallest, and the description is never cut. The column count
          follows the panel's width, not the window's (the sessions list
          beside it can be wide or folded away): three as soon as three
          cards of the minimum width fit, four only once four fit at
          full width. */}
      <Box sx={{ containerType: "inline-size" }}>
        {/* A radio group: one Tab stop (the chosen card), the arrow keys
            move the choice through the cards in reading order and wrap
            from the last card to the first, Home and End jump to the
            ends. */}
        <Box
          role="radiogroup"
          aria-label={t("new_session.intent_label")}
          onKeyDown={onIntentGridKey}
          sx={{ display: "grid", gap: `${startPage.cardGap}px`, gridAutoRows: "1fr", ...intentGridColumns }}
        >
          {orderedIntents.map((spec) => (
            <IntentCard
              key={spec.intent}
              spec={spec}
              selected={intent === spec.intent}
              onSelect={() => selectIntent(spec.intent)}
            />
          ))}
        </Box>
      </Box>
      {isModifyIntent(intent) && (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("session.primaryPicker.source_label", "Document to work on")}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={primarySource}
            onChange={(_e: React.MouseEvent<HTMLElement>, v: "upload" | "library" | null) => {
              if (v) setPrimarySource(v);
            }}
          >
            <ToggleButton value="upload">
              {t("session.primaryPicker.source_upload", "Upload a file")}
            </ToggleButton>
            <ToggleButton value="library">
              {t("session.primaryPicker.source_library", "From the document library")}
            </ToggleButton>
          </ToggleButtonGroup>
          {primarySource === "upload" && (
            <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" component="label">
                {primaryFile
                  ? t("session.primaryPicker.replace_file", "Choose another file")
                  : t("session.primaryPicker.choose_file", "Choose {{format}} file", {
                      format: (requiredFormat ?? "").toUpperCase(),
                    })}
                <input
                  hidden
                  type="file"
                  accept={requiredFormat ? `.${requiredFormat}` : undefined}
                  onChange={(e) => {
                    setCreateError(null);
                    setPrimaryFile(e.target.files?.[0] ?? null);
                  }}
                />
              </Button>
              <Typography variant="body2" color={primaryFile ? "text.primary" : "text.secondary"}>
                {primaryFile
                  ? primaryFile.name
                  : t(
                      "session.primaryPicker.upload_helper",
                      "The {{format}} you upload becomes the document the agent edits in place. Other uploads stay reference-only.",
                      { format: (requiredFormat ?? "").toUpperCase() },
                    )}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      {isModifyIntent(intent) && primarySource === "library" && (
        <FormControl size="small" fullWidth>
          <InputLabel id="primary-document-picker-label">
            {t("session.primaryPicker.label")}
          </InputLabel>
          <Select
            labelId="primary-document-picker-label"
            // ``inputProps.aria-label`` mirrors the InputLabel
            // text so getByLabelText keeps finding the control
            // even when MUI's hidden input takes the
            // accessibility focus over the rendered combobox.
            inputProps={{
              "aria-label": t("session.primaryPicker.label"),
            }}
            value={primaryDocumentId ?? ""}
            label={t("session.primaryPicker.label")}
            onChange={(e) => {
              setCreateError(null);
              const v = e.target.value;
              setPrimaryDocumentId(typeof v === "string" && v ? v : null);
            }}
          >
            {(viewableDocs.data ?? []).map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.title}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {viewableDocs.isLoading
              ? t("session.primaryPicker.loading")
              : (viewableDocs.data?.length ?? 0) === 0
                ? t(
                    requiredFormat === "pptx"
                      ? "session.primaryPicker.empty_pptx"
                      : "session.primaryPicker.empty_docx",
                  )
                : t("session.primaryPicker.helper")}
          </FormHelperText>
        </FormControl>
      )}
      {createError && (
        <Alert
          severity="error"
          onClose={() => setCreateError(null)}
        >
          {translateApiError(t, createError)}
        </Alert>
      )}
      {/* Mode (audience register) and output language are set
          inside the session — the fresh-session strip above the
          composer points at them — rather than up front here. The
          model settings open above the buttons, in one box with them,
          so the closed panel adds no gap to the page's stack. */}
      <Box>
        <Collapse in={showAdvanced}>
          <Stack sx={{ gap: 1.5, pb: 2 }}>
            {providers.length > 1 && (
              <TextField
                size="small"
                select
                label={t("session.providerPicker.label")}
                // MUI warns when the value isn't among the rendered
                // options; that's the case until the catalog loads.
                value={
                  providers.some((p) => p.slug === providerSlug)
                    ? providerSlug
                    : ""
                }
                onChange={(e) => {
                  setProviderSlug(e.target.value);
                  setProviderTouched(true);
                  setModelTouched(false);
                  setCredentialError(null);
                }}
                helperText={t("session.providerPicker.helper")}
              >
                {providers.map((p) => {
                  const enabled = isProviderEnabled(p.slug);
                  const label = (
                    <>
                      {p.display_name}
                      {p.default
                        ? t("session.providerPicker.default_suffix")
                        : ""}
                    </>
                  );
                  // The MenuItem must stay the Select's direct child —
                  // MUI reads ``value`` off its children to resolve the
                  // selection, and a Tooltip wrapper hid the value
                  // (spurious "out-of-range value" warning + a
                  // disabled marked-default rendering as blank). The
                  // tooltip therefore wraps the item's content instead.
                  return (
                    <MenuItem
                      key={p.slug}
                      value={p.slug}
                      aria-disabled={!enabled || undefined}
                      onClick={(e) => {
                        if (!enabled) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      sx={
                        !enabled
                          ? {
                              opacity: 0.5,
                              cursor: "not-allowed",
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }
                          : undefined
                      }
                    >
                      {enabled ? (
                        label
                      ) : (
                        <Tooltip
                          title={
                            <Box>
                              {t("session.providerPicker.disabled_tooltip", {
                                provider: p.display_name,
                              })}
                              .{" "}
                              <MuiLink
                                component="button"
                                color="inherit"
                                sx={{
                                  textDecoration: "underline",
                                  fontWeight: 600,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/app/account");
                                }}
                              >
                                {t(
                                  "session.providerPicker.disabled_tooltip_link",
                                )}
                              </MuiLink>
                            </Box>
                          }
                          placement="right"
                        >
                          <Box component="span" sx={{ display: "block" }}>
                            {label}
                          </Box>
                        </Tooltip>
                      )}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
            <TextField
              size="small"
              select
              label={t("session.model_field_label")}
              slotProps={SHOW_UNSET_SELECT}
              value={
                providerModelList.some((m) => m.slug === model) ||
                customOptions.some((o) => o.value === model)
                  ? model
                  : ""
              }
              onChange={(e) => {
                const slug = e.target.value;
                // MUI's Select still fires onChange for an
                // aria-disabled item: a blocked custom model (offline,
                // or without tool calling) stays unpickable here.
                if (customOptions.some((o) => o.value === slug && o.blocked !== null)) return;
                setModel(slug);
                setModelTouched(true);
                // Codex-only side-effects: seed effort/summary/
                // verbosity from the codex catalog so the rest of
                // the advanced panel stays in sync. Non-codex
                // providers don't expose these knobs, so the
                // setters fall through harmlessly.
                const next = (codexModels.data?.models ?? []).find(
                  (m) => m.slug === slug,
                );
                if (next) {
                  if (next.supported_reasoning_levels.includes("medium")) {
                    setEffort("medium");
                  } else if (next.default_reasoning_level) {
                    setEffort(next.default_reasoning_level);
                  } else {
                    setEffort("");
                  }
                  if (!next.supports_reasoning_summaries) setSummary("");
                  if (!next.support_verbosity) setVerbosity("");
                } else {
                  setEffort("");
                }
              }}
              helperText={
                customPick
                  ? t(pickedCustom?.local ? "session.model_local_hint" : "session.model_custom_hint", {
                      provider: pickedCustom?.providerName ?? "",
                    })
                  : providerModelList.find((m) => m.slug === model)?.description ??
                    (providerDefaultModel
                      ? t("session.model_default_hint", { slug: providerDefaultModel })
                      : t("session.model_provider_default_hint"))
              }
            >
              <MenuItem value="">{t("session.model_default")}</MenuItem>
              {providerModelList.map((m) => (
                <MenuItem key={m.slug} value={m.slug}>
                  {m.display_name}
                </MenuItem>
              ))}
              {customOptions.length > 0 && (
                <ListSubheader>{t("session.model_custom_group")}</ListSubheader>
              )}
              {customOptions.map((o) =>
                o.blocked === null ? (
                  <MenuItem key={o.value} value={o.value}>
                    {t("session.model_custom_option", {
                      model: o.label,
                      provider: o.providerName,
                    })}
                  </MenuItem>
                ) : (
                  // A model that can't run a session: listed, not
                  // pickable, with the reason (same pattern as a
                  // provider without credentials above).
                  <MenuItem
                    key={o.value}
                    value={o.value}
                    aria-disabled
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={{
                      opacity: 0.5,
                      cursor: "not-allowed",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <Tooltip title={blockedHint(t, o)} placement="right" describeChild>
                      <Box component="span" sx={{ display: "block" }}>
                        {blockedLabel(t, o)}
                      </Box>
                    </Tooltip>
                  </MenuItem>
                ),
              )}
            </TextField>
            {!isCodex && supportsExtendedThinking && (
              <TextField
                size="small"
                select
                label={t("session.extended_thinking_label")}
                slotProps={SHOW_UNSET_SELECT}
                value={thinkingEffort}
                onChange={(e) =>
                  setThinkingEffort(
                    e.target.value as typeof thinkingEffort,
                  )
                }
                helperText={
                  thinkingEffort === ""
                    ? t("session.extended_thinking_default")
                    : `claude --effort ${thinkingEffort}`
                }
              >
                <MenuItem value="">{t("session.model_default")}</MenuItem>
                {THINKING_EFFORTS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {reasoningEffortLabel(t, level)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {/* A custom model runs on its provider's own settings: the
                codex knobs aren't sent for it, so they aren't shown. */}
            {isCodex && !customPick && (
              <>
                <TextField
                  size="small"
                  select
                  label={t("session.reasoning_effort_label")}
                  slotProps={SHOW_UNSET_SELECT}
                  value={effort}
                  onChange={(e) => setEffort(e.target.value as typeof effort)}
                  helperText={
                    modelInfo?.default_reasoning_level
                      ? t("session.model_default_value", { value: modelInfo.default_reasoning_level })
                      : undefined
                  }
                >
                  {effortOptions.map((v) => (
                    <MenuItem key={v || "_default"} value={v}>
                      {v ? reasoningEffortLabel(t, v) : t("session.model_default")}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  label={t("session.reasoning_summary_label")}
                  slotProps={SHOW_UNSET_SELECT}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value as typeof summary)}
                  disabled={
                    modelInfo !== undefined && !modelInfo.supports_reasoning_summaries
                  }
                  helperText={
                    modelInfo && !modelInfo.supports_reasoning_summaries
                      ? t("session.model_no_reasoning_summaries", { model: modelInfo.display_name })
                      : modelInfo?.default_reasoning_summary
                        ? t("session.model_default_value", { value: modelInfo.default_reasoning_summary })
                        : undefined
                  }
                >
                  {REASONING_SUMMARY.map((v) => (
                    <MenuItem key={v || "_default"} value={v}>
                      {v ? reasoningSummaryLabel(t, v) : t("session.model_default")}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  label={t("session.verbosity_label")}
                  slotProps={SHOW_UNSET_SELECT}
                  value={verbosity}
                  onChange={(e) => setVerbosity(e.target.value as typeof verbosity)}
                  disabled={modelInfo !== undefined && !modelInfo.support_verbosity}
                  helperText={
                    modelInfo && !modelInfo.support_verbosity
                      ? t("session.model_no_verbosity", { model: modelInfo.display_name })
                      : modelInfo?.default_verbosity
                        ? t("session.model_default_value", { value: modelInfo.default_verbosity })
                        : undefined
                  }
                >
                  {VERBOSITY.map((v) => (
                    <MenuItem key={v || "_default"} value={v}>
                      {v ? verbosityLabel(t, v) : t("session.model_default")}
                    </MenuItem>
                  ))}
                </TextField>
                <ServiceTierField
                  value={serviceTier}
                  openaiAccountId={isCodex ? activeAccount?.id ?? null : null}
                  onChange={(v) => setServiceTier(v)}
                />
              </>
            )}
          </Stack>
        </Collapse>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={async () => {
              if (!canCreate || orderedIntents.length === 0) return;
              setCredentialError(null);
              setCreateError(null);
              try {
                const session = await createSession.mutateAsync({
                  // Stored data, not UI copy (see sessionTitle.ts): the
                  // lists show it in the UI language.
                  title: PLACEHOLDER_SESSION_TITLE,
                  // Explicit: the backend's top-priority branch, so
                  // what the form showed is what gets created.
                  org_slug: targetOrg?.slug,
                  // Codex still wires the personal account through
                  // ``openai_account_id``; the field is ignored for
                  // non-codex providers (Anthropic resolves its own
                  // active account server-side). A custom pick runs on
                  // the provider's own key through the gateway — never
                  // the user's personal OpenAI account, picked or not.
                  openai_account_id:
                    !customPick && isCodex ? activeAccount?.id : undefined,
                  provider: providerSlug,
                  intent,
                  // Stamp the UI language, so the session (and its
                  // documents) stay in one language whoever writes in
                  // it; draft_pr/draft_issue still answer in English
                  // server-side. Every UI language is an output
                  // language, Finnish included.
                  language: outputLanguageForUi(i18n.resolvedLanguage ?? i18n.language),
                  model: customPick ? customPick.slug : model || undefined,
                  llm_provider_id: customPick?.providerId,
                  // Codex-only knobs — the AgentTurnRequest discards
                  // these for non-codex providers, but sending them
                  // unconditionally is wasteful when we know they're
                  // codex-shaped. A custom pick sends none of them —
                  // the gateway credential carries no reasoning/
                  // verbosity knobs.
                  model_reasoning_effort: !customPick && isCodex
                    ? effort || undefined
                    : undefined,
                  model_reasoning_summary: !customPick && isCodex
                    ? summary || undefined
                    : undefined,
                  model_verbosity: !customPick && isCodex ? verbosity || undefined : undefined,
                  service_tier: !customPick && isCodex
                    ? (serviceTier || undefined) as ServiceTier | undefined
                    : undefined,
                  // Anthropic extended-thinking effort — sent only
                  // when the picked model supports it AND the user
                  // chose a value. Empty string = unset (runtime
                  // decides).
                  extended_thinking_level:
                    !customPick && !isCodex && supportsExtendedThinking && thinkingEffort !== ""
                      ? thinkingEffort
                      : undefined,
                  // Modify-in-place sessions need a primary doc the
                  // edit-skill operates on. Sent only for those
                  // intents — the backend rejects the field outright
                  // (primary_only_for_modify) on every other intent.
                  primary_document_id:
                    isModifyIntent(intent) && primarySource === "library"
                      ? primaryDocumentId
                      : undefined,
                });
                if (isModifyIntent(intent) && primarySource === "upload" && primaryFile) {
                  // The backend promotes the first upload of the
                  // intent's format to primary, so one upload is the
                  // whole handoff.
                  const form = new FormData();
                  form.append("file", primaryFile);
                  await api.post(`/sessions/${session.id}/attachments`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                }
                navigate(`/app/sessions/${session.id}`);
              } catch (err: unknown) {
                // ``missing_provider_credential:<slug>`` — the
                // credential gate's inline alert (different copy +
                // deep-link than the generic one).
                const credSlug = extractMissingProvider(err);
                if (credSlug) {
                  setCredentialError(credSlug);
                  return;
                }
                // Primary-document validation errors (and other
                // structured 4xx detail strings) surface inline via
                // the i18n helper from Task 6 so the user can fix
                // the form without losing their selections.
                const detail = extractErrorDetail(err);
                if (detail) {
                  setCreateError(detail);
                  return;
                }
                throw err;
              }
            }}
            // No catalog yet (or it failed): no card is shown, so there is
            // no intent to start.
            disabled={!canCreate || createSession.isPending || orderedIntents.length === 0}
          >
            {t("new_session.start_button")}
          </Button>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            {showAdvanced ? t("new_session.advanced_hide") : t("new_session.advanced_show")}
          </Button>
        </Stack>
      </Box>
      {/* Which org this lands in — stated before the click, not
          discovered afterwards. ``targetOrg`` is the slug the
          request sends, so this can't drift from reality. */}
      {targetOrg && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
          {targetOrg.skippedPin
            ? t("session.create_org_pin_not_member", {
                org: targetOrg.name,
                pinned: targetOrg.skippedPin,
                defaultValue:
                  "Will be created in {{org}}, your own organisation. {{pinned}} is selected only to administer it, so new sessions don't use its credits or settings.",
              })
            : targetOrg.reason === "pinned"
            ? t("session.create_org_pinned", {
                org: targetOrg.name,
                defaultValue:
                  "Will be created in {{org}} — the organisation you have selected.",
              })
            : targetOrg.reason === "only-membership"
              ? t("session.create_org_only", {
                  org: targetOrg.name,
                  defaultValue:
                    "Will be created in {{org}} — your only organisation. Use the switcher above to change it.",
                })
              : t("session.create_org_default", {
                  org: targetOrg.name,
                  defaultValue:
                    "Will be created in {{org}}. Use the switcher above to pick a different organisation.",
                })}
        </Typography>
      )}
    </Stack>
  );
}

/** ``workspace-write`` → ``workspace_write``: the catalog key of an
 *  intent's sandbox or approval policy. */
function metaKey(value: string): string {
  return value.replace(/-/g, "_");
}

const INTENT_ICONS: Record<Intent, React.ReactElement> = {
  document: <DescriptionOutlinedIcon fontSize="small" />,
  review: <FactCheckOutlinedIcon fontSize="small" />,
  summarize: <NotesOutlinedIcon fontSize="small" />,
  draft_pr: <MergeTypeOutlinedIcon fontSize="small" />,
  draft_issue: <BugReportOutlinedIcon fontSize="small" />,
  document_modify: <EditDocumentIcon fontSize="small" />,
  presentation: <SlideshowOutlinedIcon fontSize="small" />,
  presentation_modify: <EditNoteOutlinedIcon fontSize="small" />,
};

/** Intent picker card, a radio in the grid's radio group. Selecting a
 *  card commits the intent; the grid handles one-of-N selection (and
 *  the arrow keys) by passing ``selected`` for the active card only. The category tag sits top
 *  right; the chip row at the bottom shows the two decision-relevant
 *  attributes (sandbox, approval) so the user can tell "read-only, no
 *  approvals" from "edits files and asks first" without opening docs.
 *  The "Chat" chip is gone: every intent runs as a chat, so it said
 *  nothing, and it read as a second category next to the tag. */
function IntentCard({
  spec,
  selected,
  onSelect,
}: {
  spec: IntentSpec;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const label = t(`intents.${spec.intent}`, spec.label);
  const description = t(
    `intent_descriptions.${spec.intent}`,
    spec.description,
  );
  const category = INTENT_CATEGORIES[spec.intent];
  const chipSx = { height: 20, fontSize: 10, "& .MuiChip-label": { px: "5px" } };
  const id = useId();
  const titleId = `${id}-title`;
  const tagId = `${id}-tag`;
  const descriptionId = `${id}-description`;
  const chipsId = `${id}-chips`;
  return (
    <Card
      variant="outlined"
      onClick={onSelect}
      // A radio in the grid's radio group: named by its title alone, and
      // described by the category, the description and the chips.
      role="radio"
      aria-checked={selected}
      aria-labelledby={titleId}
      aria-describedby={[category ? tagId : null, descriptionId, chipsId].filter(Boolean).join(" ")}
      // Roving tabindex: only the chosen card is a Tab stop; the group's
      // arrow keys move between cards.
      tabIndex={selected ? 0 : -1}
      data-testid={`intent-card-${spec.intent}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        cursor: "pointer",
        borderRadius: "10px",
        borderColor: selected ? "text.primary" : "divider",
        // A 2 px selected edge without moving the layout: the second
        // pixel is an inset ring.
        boxShadow: selected ? (theme) => `inset 0 0 0 1px ${theme.palette.text.primary}` : "none",
        bgcolor: selected ? "action.selected" : "transparent",
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": {
          borderColor: selected ? "text.primary" : "text.secondary",
        },
        "&:focus-visible": {
          outline: `2px solid ${repoTokens.colors.focus}`,
          outlineOffset: 2,
        },
      }}
    >
      {category && (
        // The category as a tab in the card's top-right corner: above the
        // title line, so a long title never wraps around it.
        <Typography
          component="span"
          id={tagId}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: "18px",
            px: 1,
            borderBottomLeftRadius: "6px",
            bgcolor: repoTokens.colors.elevated,
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {t(INTENT_CATEGORY_TAG[category])}
        </Typography>
      )}
      <Stack sx={{ gap: 0.75, flex: 1, px: 1.25, pt: 2.25, pb: 1.25 }}>
        <Stack direction="row" sx={{ alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ display: "flex", flexShrink: 0, pt: "1px" }}>
            {INTENT_ICONS[spec.intent] ?? <DescriptionOutlinedIcon fontSize="small" />}
          </Box>
          <Typography
            id={titleId}
            sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35, flex: 1, minWidth: 0 }}
          >
            {label}
          </Typography>
        </Stack>
        {/* The whole description, never clamped. Cards in the grid share
            the tallest one's height, and the chips sit at the bottom, so
            the rows line up. */}
        <Typography
          id={descriptionId}
          sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.35 }}
          color="text.secondary"
        >
          {description}
        </Typography>
        <Stack
          id={chipsId}
          direction="row"
          sx={{ gap: 0.5, mt: "auto", pt: 0.5, flexWrap: "nowrap", minWidth: 0 }}
        >
          <Chip
            size="small"
            variant="outlined"
            color={
              spec.sandbox === "danger-full-access"
                ? "error"
                : spec.sandbox === "workspace-write"
                  ? "success" // the green status accent
                  : "default"
            }
            label={t(`new_session.meta.sandbox.${metaKey(spec.sandbox)}`, {
              defaultValue: spec.sandbox,
            })}
            sx={chipSx}
          />
          <Chip
            size="small"
            variant="outlined"
            color={spec.approval === "on-request" ? "success" : "default"}
            label={t(`new_session.meta.approval.${metaKey(spec.approval)}`, {
              defaultValue: spec.approval,
            })}
            sx={{ ...chipSx, minWidth: 0 }}
          />
        </Stack>
      </Stack>
    </Card>
  );
}
